import { createMachine, assign, send } from 'xstate';
import CheckoutContext from '../Context/CheckoutContext';

import GetParamURL from '../Helpers/GetParamURL';
import { addGlobalContext, sendEventRUM, setUserDD, trackCustomEventWarning } from '../Helpers/DataDog';

import { guardarDomicilio } from '../Services/guardarDomicilio';
import {ObtenerCampaniasService} from '../Services/obtenerCampaniasService';
import ObtenerMontoACobrar from '../Services/ObtenerMontoACobrarService';
import ObtenerDatosFiliatoriosService from '../Services/ObtenerDatosFiliatoriosService';
import obtenerDomicilios from '../Services/obtenerDomicilios';
import ObtenerDescripcionAlcancesDelPlan from '../Services/ObtenerDescripcionAlcancesDelPlan';
import NotificationSevice from '../Services/NotificationSevice';
import URL from '../Helpers/Url';
import CheckoutStepper from '../Models/CheckoutStepper';
import GoogleAnalytics from '../Helpers/GoogleAnalytics';

import ValidarPlanDeVentaHandler from './Actions/ValidarPlanDeVentaHandler';
import ValidarPlanDeVentaDigital from '../Services/ValidarPlanDeVentaDigital';

const { Modo, Combo, URLRef, canalDeVenta, productCategory, formaDeContacto } = GetParamURL();

export const CheckoutMachine = createMachine({
    id: 'checkout',
    initial: 'validarPlanDeVenta',
    context: CheckoutContext,
    states: {
        validarPlanDeVenta: {
            entry: assign({
                modo: () => Modo,
                combo: () => parseInt(Combo),
                URLRef: () => URLRef,
                canalDeVenta: () => parseInt(canalDeVenta),
                formaDeContacto: () => parseInt(formaDeContacto),
                productCategory: () => productCategory
            }),
            invoke: {
                id: 'validarPlanDeVenta',
                src: (context) => {
                    return ValidarPlanDeVentaDigital({ combo: context.combo });
                },
                onDone: {
                    actions: [
                        assign((context, event) => {
                            return {
                                responseValidarPlanDeVenta: ValidarPlanDeVentaHandler(event),
                                isLoading: false
                            };
                        }),
                        send((context, event) => {
                            return {
                                type: context.responseValidarPlanDeVenta,
                            };
                        }),
                    ],
                },
                onError: {
                    actions: [
                        assign((_, event) => {
                            return { error: event.data };
                        }),
                        send(() => {
                            return {
                                type: 'ERRORVALIDARPLAN',
                            };
                        }),
                    ],
                },
            },
            on: {
                OBTENERCAMPANIA: 'loadingObtenerCampania',
                PAGOPENDIENTE: 'pagoPendiente',
                YASOSSUSCRIPTOR: 'yaSosSuscriptor',
                REDIRECTPAYWALL: 'redirectPaywall',
                ERRORVALIDARPLAN: 'error',
            },
        },
        loadingObtenerCampania: {
            invoke: {
                id: 'obtenerCampania',
                src: (context) => {
                    const campaniasActivas = context.isBonusSale || context.combo === context.comboBonificado ? context.campaniasBonificadasActivas : context.campaniasActivas;
                    return ObtenerCampaniasService(campaniasActivas);
                },
                onDone: {
                    actions: [
                        assign((context, event) => {
                            if (!event.data || !event.data.response || event.data.code != '0000') return;

                            let matching = event.data.response.find((item) =>
                                item.grupoPlanes.find((plan) => (context.isBonusSale ? plan.combo == context.comboBonificado : plan.combo == context.combo))
                            );
                            if (!matching) return;

                            context.campaniaId = matching.id;
                            let planCampaniaSeleccionado = matching.grupoPlanes.find((plan) => (context.isBonusSale ? plan.combo == context.comboBonificado : plan.combo == context.combo));

                            return {
                                planCampania: planCampaniaSeleccionado,
                                datosPersonales: {
                                    ...context.datosPersonales,
                                    comboElegido: context.combo,
                                },
                            };
                        }),
                        send((context, event) => {
                            if (!event.data || event.data.code === '0001' || !context.planCampania || context.planCampania === undefined) {
                                return {
                                    type: 'ERROR',
                                };
                            } else {
                                if (context.isBonusSale) {
                                    return {
                                        type: 'LOADINGOBTENERDATOSFILIATORIOS',
                                    };
                                } else {
                                    return {
                                        type: 'LOADINGOBTENERDESCRIPCIONPLAN',
                                    };
                                }
                            }
                        }),
                    ],
                },
                onError: {
                    actions: assign((_, event) => {
                        return { error: event.data };
                    }),
                },
            },
            on: {
                LOADINGOBTENERDATOSFILIATORIOS: 'loadingObtenerDatosFiliatorios',
                LOADINGOBTENERDESCRIPCIONPLAN: 'loadingObtenerDescripcionPlan',
                ERROR: 'error',
            },
        },
        loadingObtenerDescripcionPlan: {
            invoke: {
                id: 'obtenerDescripcionPlan',
                src: (context) => ObtenerDescripcionAlcancesDelPlan({ idplan: context.planCampania.id }),
                onDone: {
                    actions: [
                        assign((_, event) => {
                            return {
                                planBeneficios: event.data?.response,
                            };
                        }),
                        send(() => {
                            return {
                                type: 'LOADINGOBTENERMONTOACOBRAR',
                            };
                        }),
                    ],
                },
                onError: {
                    actions: [
                        assign((_, event) => {
                            return { error: event.data };
                        }),
                        send(() => {
                            return {
                                type: 'ERROR',
                            };
                        }),
                    ],
                },
            },
            on: {
                ERROR: 'error',
                LOADINGOBTENERMONTOACOBRAR: 'loadingObtenerMontoACobrar',
            },
        },
        loadingObtenerMontoACobrar: {
            invoke: {
                id: 'obtenerMontoACobrar',
                src: (context) => {
                    let payload = {
                        idPlanVta: context.planCampania.id,
                        idCampania: context.campaniaId,
                    };
                    return ObtenerMontoACobrar(payload);
                },
                onDone: {
                    actions: [
                        assign((context, event) => {
                            if (event.data?.code === '0000' && event.data?.response) {
                                const isPlanAnual = context?.planCampania?.planes[0]?.detalles[0]?.productos[0]?.periodicidad === 360;
                                const mesActual = new Date().getMonth();
                                const mesSiguiente = mesActual + 1;
                                const precioPlanAnualFormated = context?.planCampania?.planes[0]?.precio.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                                const dataResponse = {
                                    isPlanAnual: isPlanAnual,
                                    montoTotal: !isPlanAnual ? Math.round(event.data.response.Total) : precioPlanAnualFormated,
                                    montoProporcional: Math.round(event.data.response.Proporcional),
                                    montoMesSiguiente: Math.round(event.data.response.MontoMesSiguiente),
                                    tieneClub: context?.planCampania?.nombre.toLowerCase().includes('club'),
                                    mesActual: mesActual,
                                    mesSiguiente: mesSiguiente,
                                    mesesDescuento: context?.planCampania?.planes[0]?.mesesDescuento,
                                    montoFinal: !isPlanAnual && context?.planCampania?.planes[0]?.precioSinDescuento,
                                    beneficios: context?.planBeneficios,
                                };
                                return {
                                    dataObtenerMontoService: event.data.response,
                                    datosCarrito: dataResponse,
                                };
                            }
                        }),
                        send((context, event) => {
                            if (event.data.code === '0000') {
                                return {
                                    type: 'OBTENERDATOSFILIATORIOS',
                                };
                            }
                            return {
                                type: 'ERROR',
                            };
                        }),
                    ],
                },
                onError: {
                    actions: assign((_, event) => {
                        return { error: event.data };
                    }),
                },
            },
            on: {
                OBTENERDATOSFILIATORIOS: 'loadingObtenerDatosFiliatorios',
                VENTABONIFICADA: 'ventaBonificada',
                ERROR: 'error',
            },
        },
        loadingObtenerDatosFiliatorios: {
            invoke: {
                id: 'obtenerDatosFiliatorios',
                src: (context) => {
                    if(context.productCategory === 'Campanita'){
                        const nombrePlan = context.planCampania?.planes[0]?.nombre ?? null;
                        GoogleAnalytics.trackPageBell("formulario-datos-personales", canalDeVenta, formaDeContacto, nombrePlan);
                    }
                    return ObtenerDatosFiliatoriosService();
                },
                onDone: {
                    actions: [
                        assign((context, event) => {
                            return {
                                datosPersonales:
                                    event.data.code === '0000'
                                        ? {
                                              ...context.datosPersonales,
                                              ...event.data.response,
                                              residencia: event.data.response.domicilios
                                                  ? event.data.response.domicilios.length === 0 || event.data.response.domicilios.some((domicilio) => domicilio.pais === 'ARGENTINA')
                                                      ? 'Argentina'
                                                      : 'Exterior'
                                                  : '',
                                          }
                                        : {
                                              ...context.datosPersonales,
                                          },
                                domicilio: event.code === '0000' && event.data.response.domicilios,
                                usuarioPrecargado: !!event.data.response?.crmId && event.data.response?.numeroDocumento,
                                isLoading: false,
                                showCart: true,
                                enabledDomicilio:
                                    event.data.code === '0000' &&
                                    event.data.response.domicilios &&
                                    (event.data.response.domicilios.length === 0 || event.data.response.domicilios.some((domicilio) => domicilio.pais === 'ARGENTINA')),
                            };
                        }),
                        send((context) => {
                            //se valida si es una ventaBonificada o flujo normal
                            if (context.isBonusSale) {
                                return { type: 'VENTABONIFICADA' };
                            }
                            return { type: 'SAVESTEPINFORMATION_1' };
                        }),
                    ],
                },
                onError: {
                    actions: assign((_, event) => {
                        return { error: event.data };
                    }),
                },
            },
            on: {
                SAVESTEPINFORMATION_1: 'saveStepInformation_1',
                VENTABONIFICADA: 'ventaBonificada'

            }
        },
        saveStepInformation_1: {
            invoke: {
                src: (context) => {
                    return NotificationSevice.saveStepInformation(context, CheckoutStepper.formularioDatosPersonales);
                },
                onDone: {
                    actions: [
                        assign((context, event) => {
                            if (event.data.code === '0000') {
                                return {
                                    correlationId: event.data.response
                                }
                            }
                            else {
                                trackCustomEventWarning('saveStepInformation_1', event);
                            }
                        }),
                        send((context, event) => {
                            if (context.datosPersonales.crmId && context.datosPersonales.numeroDocumento) {
                                context.datosPersonales.terminos = true;
                                context.skipPersonalDataForm = true;
                                addGlobalContext(context, 'Salteo Formulario Datos Personales');
                                //Se envian logs de los formularios de datos personales que se saltean para el Funnel de checkout en datadog.
                                sendEventRUM('Formulario Datos Personales');
                                sendEventRUM('Guarda datos personales');
                                return { type: 'REALIZARPAGO' };
                            }
                            //si no continua por el flujo normal de compra
                            return { type: 'DATOSPERSONALES' };
                        })]
                },
                onError: {
                    actions: [
                        assign((context, event) => {
                            trackCustomEventWarning('saveStepInformation_1', event);
                        }),
                        send((context, event) => {
                            if (context.datosPersonales.crmId && context.datosPersonales.numeroDocumento) {
                                context.datosPersonales.terminos = true;
                                context.skipPersonalDataForm = true;
                                addGlobalContext(context, 'Salteo Formulario Datos Personales');
                                //Se envian logs de los formularios de datos personales que se saltean para el Funnel de checkout en datadog.
                                sendEventRUM('Formulario Datos Personales');
                                sendEventRUM('Guarda datos personales');
                                return { type: 'REALIZARPAGO' };
                            }
                            //si no continua por el flujo normal de compra
                            return { type: 'DATOSPERSONALES' };
                        }),
                    ],
                }
            },
            on: {
                DATOSPERSONALES: 'datosPersonales',
                REALIZARPAGO: 'realizarPago'
            }
        },
        datosPersonales: {
            invoke: {
                id: 'personalDataRUM',
                src: () => sendEventRUM('Formulario Datos Personales')
            },
            on: {
                CONTINUAR: {
                    target: 'validarDatosPersonales',
                    actions: assign((_, event) => {
                        sendEventRUM('Guarda datos personales');
                        return {
                            enabledDomicilio: event.enabledDomicilio,
                            datosPersonales: event.datosPersonales,
                            isLoading: true,
                        };
                    }),
                },
                INPUTSPARCIALES: {
                    actions: assign({
                        datosPersonales: (context, event) => {
                            return event.datosPersonales;
                        },
                        erroresDatosPersonales: (context, event) => {
                            return event.erroresDatosPersonales;
                        },
                    }),
                },
                ERROR: 'error',
            },
        },
        validarDatosPersonales: {
            invoke: [
                {
                    src: (context) => addGlobalContext(context, context.planCampania.planes[0].detalles[0].familiaProducto.nombre),
                },
                {
                    id: 'validarDatosPersonales',
                    src: (context, _) => {
                        let data = {
                            ...context.datosPersonales,
                        };
                        return ValidarPlanDeVentaDigital(data);
                    },
                    onDone: {
                        actions: [
                            assign((context, event) => {
                                return {
                                    responseFlow: ValidarPlanDeVentaHandler(event),
                                    datosPersonales: {
                                        ...context.datosPersonales,
                                        ...event.data.response,
                                        mailOfuscado: event.data.message,
                                    },
                                    isLoading: !event?.data?.response?.esDNIValido && (event?.data?.response?.errorDNIExiste || event?.data?.response?.errorDNIInvalido) ? false : true,
                                    showToastPersonalData: ValidarPlanDeVentaHandler(event) === 'DATOSPERSONALES',
                                    showToastDniInvalido: ValidarPlanDeVentaHandler(event) === 'DOCUMENTOINVALIDO',
                                };
                            }),
                            send((context, event) => {
                                //TODO: validar el context cuando se tenga que redirigir a otra ruta
                                return {
                                    type: context.responseFlow,
                                };
                            }),
                        ],
                    },
                    onError: {
                        target: 'datosPersonales',
                        actions: [
                            assign((_, event) => {
                                return { error: event.data };
                            }),
                            send((context, event) => {
                                return {
                                    type: 'ERRORFLOW',
                                };
                            }),
                        ],
                    },
                },
            ],
            on: {
                DATOSPERSONALES: 'datosPersonales',
                VALIDARSUSCRIPCIONES: 'validarSuscripciones',
                PAGOPENDIENTE: 'pagoPendiente',
                ERRORFLOW: 'error',
                DOCUMENTOINVALIDO: 'datosPersonales',
            },
        },
        validarSuscripciones: {
            invoke: {
                id: 'validarSuscripciones',
                src: () => {
                    let data = {
                        combo: parseInt(Combo),
                    };
                    return ValidarPlanDeVentaDigital(data);
                },
                onDone: {
                    actions: send((context, event) => {
                        if (event.data) {
                            if (event.data.response.esPlanCompatible) {
                                return {
                                    type: 'REALIZARPAGO',
                                };
                            }
                            if (event.data.response.tieneDeuda) {
                                return {
                                    type: 'PAGOPENDIENTE',
                                };
                            }
                        } else {
                            return {
                                type: 'REALIZARPAGO',
                            };
                        }
                    }),
                },
                onError: {
                    actions: [
                        assign((_, event) => {
                            return { error: event.data };
                        }),
                        send(() => {
                            return {
                                type: 'ERRORVALIDARSUSCRIPCIONES',
                            };
                        }),
                    ],
                },
            },
            on: {
                REALIZARPAGO: 'realizarPago',
                PAGOPENDIENTE: 'pagoPendiente',
                YASOSSUSCRIPTOR: 'yaSosSuscriptor',
                ERRORVALIDARSUSCRIPCIONES: 'error',
            },
        },
        realizarPago: {
            invoke: [
                {
                    id: 'formularioPagoRUM',
                    src: () => sendEventRUM('Formulario Pago'),
                },
            ],
            on: {
                CONTINUARNOINFORMA: {
                    target: 'gracias',
                    actions: assign((_, event) => {
                        return {
                            dataVenta: event.dataVenta,
                        };
                    }),
                },
                CONTINUAREXTRANJERO: 'submitDomicilio',
                CONTINUARVENTABACKGROUND: 'gracias',
                CONTINUARARGENTINO: {
                    target: 'obtenerDomiciliosGuardados',
                    actions: assign((_, event) => {
                        return {
                            dataVenta: event.dataVenta,
                            isLoading: true,
                        };
                    }),
                },
                CANCELAR: {
                    target: 'saveStepInformation_1',
                    actions: assign((_, event) => {
                        return {
                            isLoading: false,
                            datosPersonales: {
                                ...event.datosPersonales,
                                crmId: event.crmId
                            },
                            showToastErrorWondersoft: event.showToastErrorWondersoft
                        };
                    }),
                },
                ERROR: 'error',
            },
        },
        obtenerDomiciliosGuardados: {
            invoke: [
                {
                    id: 'saveStepInformation_3',
                    src: (context) => NotificationSevice.saveStepInformation(context, CheckoutStepper.formularioDomicilio)
                },
                {
                    id: 'obtenerDomicilios',
                    src: () => obtenerDomicilios(),
                    onDone: {
                        actions: [
                            assign((_, event) => {
                                return {
                                    domicilio: event.data.code === '0000' && event.data.response.Domicilios,

                                    isLoading: false,
                                };
                            }),
                            send((context, event) => {
                                if (context.domicilio.length > 0) {
                                    return {
                                        type: 'SELECCIONARDOMICILIO',
                                    };
                                } else {
                                    return {
                                        type: 'CONTINUAR',
                                    };
                                }
                            }),
                        ],
                    },
                    onError: {
                        actions: assign((_, event) => {
                            return { error: event.data };
                        }),
                    },
                },
            ],
            on: {
                CONTINUAR: 'ingresarDomicilio',
                SELECCIONARDOMICILIO: 'domiciliosGuardados',
                CANCELAR: 'realizarPago',
            },
        },
        domiciliosGuardados: {
            on: {
                CONTINUAR: {
                    target: 'ingresarDomicilio',
                    actions: assign({
                        agregarDomicilio: (_, event) => event.agregarDomicilio,
                    }),
                },
                SUBMIT: {
                    target: 'submitDomicilio',
                    actions: assign((_, event) => {
                        return {
                            dataDomicilio: event.dataDomicilio,
                            isLoading: true,
                        };
                    }),
                },
                CANCELAR: 'realizarPago',
            },
        },
        ingresarDomicilio: {
            on: {
                SUBMIT: {
                    target: 'submitDomicilio',
                    actions: assign((_, event) => {
                        return {
                            dataDomicilio: event.dataDomicilio,
                            isLoading: true,
                        };
                    }),
                },
                VOLVER: {
                    target: 'domiciliosGuardados',
                },
            },
        },
        submitDomicilio: {
            invoke: [
                {
                    id: 'submitDomicilio',
                    src: guardarDomicilio,
                    onError: {
                        target: 'error',
                        actions: assign((context, event) => {
                            return {
                                errorMessage: event.data,
                            };
                        }),
                    },
                    onDone: {
                        target: 'gracias',
                        actions: assign((_, event) => {
                            return {
                                dataDomicilio: event.data?.response,
                                isLoading: false,
                            };
                        }),
                    },
                },
                {
                    id: 'guardarDomicilioRUM',
                    src: () => sendEventRUM('Guarda datos domicilio'),
                },
            ],
        },
        ventaBonificada: {
            on: {
                ERROR: 'error',
                SUBMIT: 'yaSosSuscriptor'
            },
            // type: 'final',
        },
        gracias: {
            invoke: [
                {
                    //Guardo el paso de finalizar venta en el gracias.
                    id: 'saveFinalStep',
                    src: (context) => NotificationSevice.saveStepInformation(context, CheckoutStepper.ventaFinalizada)
                },
                {
                    id: 'confirmacionRUM',
                    src: () => sendEventRUM('Gracias'),
                }
            ],
            type: 'final',
        },
        pagoPendiente: {
            invoke: [
                {
                    id: 'saveCancelationSale',
                    src: (context) => NotificationSevice.saveStepInformation(context, CheckoutStepper.ventaCancelada)
                }
            ],
            type: 'final',
        },
        yaSosSuscriptor: {
            invoke: [
                {
                    id: 'saveCancelationSale',
                    src: (context) => NotificationSevice.saveStepInformation(context, CheckoutStepper.ventaCancelada)
                }
            ],
            type: 'final',
        },
        error: {
            invoke: [
                {
                    id: 'saveCancelationSale',
                    src: (context) => NotificationSevice.saveStepInformation(context, CheckoutStepper.ventaCancelada)
                }
            ],
            type: 'final',
        },
        redirectPaywall: {
            entry: (context) => {
                addGlobalContext(context, 'Plan insuficiente');
                return (location.href = URL.RedirectPaywallPlanInsuficiente(context.canalDeVenta, context.formaDeContacto));
            },
            type: 'final',
        }
    },
});
